import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Profile as SharedProfile } from '@shared/types/profile';

// Store-specific extensions of shared types
// These add UI-specific fields (id, current) for local state management
export interface ProfileExperience {
  id: string;
  company: string;
  position: string;
  startDate: string;
  endDate: string | null;
  description: string;
  current: boolean;
}

export interface ProfileEducation {
  id: string;
  institution: string;
  degree: string;
  fieldOfStudy: string;
  startDate: string;
  endDate: string | null;
  current: boolean;
}

export interface ProfileCertification {
  id: string;
  name: string;
  issuer: string;
  issueDate: string;
  expiryDate: string | null;
  credentialId: string | null;
}

// Store-specific Profile type for local state
// Note: API uses SharedProfile, this is for UI state only
export interface Profile {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  location: string | null;
  summary: string | null;
  experience: ProfileExperience[];
  education: ProfileEducation[];
  skills: string[];
  certifications: ProfileCertification[];
  languages: string[];
  lastATSScore?: number;
  lastATSAnalyzedAt?: string;
  createdAt: Date;
  updatedAt: Date;
}

// Type alias for clarity - this is the shared API type
export type APIProfile = SharedProfile;

/** Conflict state when both local + server profiles exist after login */
export interface ProfileConflict {
  localProfile: Profile;
  serverProfile: Profile;
}

interface ProfileState {
  profile: Profile | null;
  isLoading: boolean;
  error: string | null;
  isLocalMode: boolean; // true if profile is only stored locally
  hasHydrated: boolean; // true after initial load from localStorage
  conflict: ProfileConflict | null; // non-null while conflict modal is shown
}

interface ProfileActions {
  setProfile: (profile: Profile | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  updateSection: <K extends keyof Profile>(section: K, data: Profile[K]) => void;
  clearProfile: () => void;
  setLocalMode: (isLocal: boolean) => void;
  setHydrated: (hydrated: boolean) => void;
  setConflict: (conflict: ProfileConflict | null) => void;
}

export type ProfileStore = ProfileState & ProfileActions;

export const useProfileStore = create<ProfileStore>()(
  persist(
    (set) => ({
      // State
      profile: null,
      isLoading: false,
      error: null,
      isLocalMode: false, // Start in cloud mode
      hasHydrated: false,
      conflict: null,

      // Actions
      setProfile: (profile) => set({ profile, error: null }),

      setLoading: (loading) => set({ isLoading: loading }),

      setError: (error) => set({ error }),

      updateSection: (section, data) =>
        set((state) => ({
          profile: state.profile
            ? { ...state.profile, [section]: data, updatedAt: new Date() }
            : null,
        })),

      clearProfile: () => set({ profile: null, error: null }),

      setLocalMode: (isLocal) => set({ isLocalMode: isLocal }),

      setConflict: (conflict) => set({ conflict }),

      setHydrated: (hydrated) => set({ hasHydrated: hydrated }),
    }),
    {
      name: 'profile-storage',
      partialize: (state) => ({
        profile: state.profile,
        isLocalMode: state.isLocalMode,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          state.setHydrated(true);
        }
      },
    }
  )
);
